/* eslint camelcase: "off" */
/**
 * People — searchable list of employees with verification history,
 * each linking to a per-person deep-dive page.
 *
 * Wired to `verificationClient.listPeople()` (django-monolith
 * /verification/admin/people/).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  InputField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@jem-open/jem-ui';
import { Loader2 } from 'lucide-react';

import apiResponseHandler from '../../../../../utils/apiResponseHandler';
import {
  FraudSeverity,
  PersonAggregate,
  verificationClient,
} from '../../../../../services/verificationClient';
import { severityTagVariant } from './severity';


const PeopleTab: React.FC = () => {
  const history = useHistory();
  const requestIdRef = useRef(0);
  const [search, setSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState<string>('_all');
  const [rows, setRows] = useState<PersonAggregate[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setLoading(true);
    try {
      const params: Parameters<typeof verificationClient.listPeople>[0] = {};
      if (riskFilter !== '_all') params.risk = riskFilter as FraudSeverity;
      if (search) params.search = search;
      const page = await verificationClient.listPeople(params);
      if (requestId !== requestIdRef.current) return;
      setRows(page.results);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      apiResponseHandler.handleApiError(err);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [riskFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered: PersonAggregate[] = rows;

  return (
    <div>
      {/* Filters */}
      <div className="mb-sm grid grid-cols-1 gap-sm rounded-2xl border border-greyscale-border-default bg-white p-md md:grid-cols-3">
        <div className="md:col-span-2">
          <InputField
            label="Search"
            value={search}
            placeholder="Employee ID, name, employer, BSUID, MSISDN"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-xxs">
          <span className="text-xs text-greyscale-text-caption">Risk score</span>
          <Select value={riskFilter} onValueChange={setRiskFilter}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="ok">OK</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* People table */}
      <div className="rounded-2xl border border-greyscale-border-default bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Employer</TableHead>
              <TableHead>Risk</TableHead>
              <TableHead>Sessions</TableHead>
              <TableHead>Mismatches</TableHead>
              <TableHead>Open signals</TableHead>
              <TableHead>Last seen</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.employee_id}>
                <TableCell>
                  <div className="text-xs font-medium">{p.employee_name}</div>
                  <div className="text-xxs text-greyscale-text-caption">
                    #{p.employee_id} · {p.msisdn}
                  </div>
                </TableCell>
                <TableCell className="text-xs">{p.employer_name}</TableCell>
                <TableCell>
                  <div className="opacity-50">
                    <Tag variant={severityTagVariant(p.risk_score)}>{p.risk_score}</Tag>
                  </div>
                </TableCell>
                <TableCell className="text-xs">{p.session_count}</TableCell>
                <TableCell>
                  {p.mismatch_count > 0 ? (
                    <Tag variant="failed">{p.mismatch_count}</Tag>
                  ) : (
                    <span className="text-xs text-greyscale-text-caption">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {p.open_signals.length > 0 ? (
                    <div className="flex flex-wrap gap-xxs">
                      {p.open_signals.map((s) => (
                        <Tag key={s} variant="failed">
                          {s}
                        </Tag>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-greyscale-text-caption">none</span>
                  )}
                </TableCell>
                <TableCell className="text-xs">
                  {new Date(p.last_seen).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Button
                    variant="link"
                    size="xs"
                    onClick={() =>
                      history.push(`/admin/employees/${p.employee_id}`)
                    }
                  >
                    open →
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-md text-center text-greyscale-text-caption">
                  No people match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && (
          <div className="flex justify-center py-sm">
            <Loader2 className="animate-spin text-secondary-text-label" size={20} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PeopleTab;
